import './App.css';
import React, { Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import DevUpIcon from "./assets/devup_transparent_v1.png";
import { upcomingTalks } from './content/talks';
const MainContent = React.lazy(() => import('./pages/MainContent'));
const SupportContent = React.lazy(() => import('./pages/SupportContent'));
const PrivacyContent = React.lazy(() => import('./pages/PrivacyContent'));
const CampaignLanding = React.lazy(() => import('./pages/CampaignLanding'));
const NIS2Compliance = React.lazy(() => import('./pages/NIS2Compliance'));
const AIProductivityStartsBeforePrompt = React.lazy(() => import('./pages/AIProductivityStartsBeforePrompt'));
const PartnerContent = React.lazy(() => import('./pages/PartnerContent'));
const AboutContent = React.lazy(() => import('./pages/AboutContent'));
const HeliumLaunchEvent = React.lazy(() => import('./pages/HeliumLaunchEvent'));

function App() {
  const [showMenu, setShowMenu] = React.useState(false);
  const [showDesktopUseCases, setShowDesktopUseCases] = React.useState(false);
  const desktopUseCasesRef = React.useRef<HTMLDivElement | null>(null);
  const desktopUseCasesCloseTimeoutRef = React.useRef<number | null>(null);
  const [showDesktopEvent, setShowDesktopEvent] = React.useState(false);
  const desktopEventRef = React.useRef<HTMLDivElement | null>(null);
  const desktopEventCloseTimeoutRef = React.useRef<number | null>(null);
  const location = useLocation();

  const clearDesktopUseCasesCloseTimeout = () => {
    if (desktopUseCasesCloseTimeoutRef.current !== null) {
      window.clearTimeout(desktopUseCasesCloseTimeoutRef.current);
      desktopUseCasesCloseTimeoutRef.current = null;
    }
  };

  const openDesktopUseCasesMenu = () => {
    clearDesktopUseCasesCloseTimeout();
    setShowDesktopUseCases(true);
  };

  const scheduleDesktopUseCasesClose = () => {
    clearDesktopUseCasesCloseTimeout();
    desktopUseCasesCloseTimeoutRef.current = window.setTimeout(() => {
      setShowDesktopUseCases(false);
    }, 140);
  };

  const closeDesktopUseCasesMenu = () => {
    clearDesktopUseCasesCloseTimeout();
    setShowDesktopUseCases(false);
  };

  const clearDesktopEventCloseTimeout = () => {
    if (desktopEventCloseTimeoutRef.current !== null) {
      window.clearTimeout(desktopEventCloseTimeoutRef.current);
      desktopEventCloseTimeoutRef.current = null;
    }
  };

  const openDesktopEventMenu = () => {
    clearDesktopEventCloseTimeout();
    setShowDesktopEvent(true);
  };

  const scheduleDesktopEventClose = () => {
    clearDesktopEventCloseTimeout();
    desktopEventCloseTimeoutRef.current = window.setTimeout(() => {
      setShowDesktopEvent(false);
    }, 140);
  };

  const closeDesktopEventMenu = () => {
    clearDesktopEventCloseTimeout();
    setShowDesktopEvent(false);
  };

  React.useEffect(() => {
    return () => {
      clearDesktopUseCasesCloseTimeout();
      clearDesktopEventCloseTimeout();
    };
  }, []);

  type MenuItem = {
    label: string;
    href: string;
    external?: boolean;
  };

  const offerMenuItems: MenuItem[] = [
    { label: 'Home', href: '/' },
    { label: 'Offers', href: '/#offers' },
    { label: 'Azure Health Check', href: '/campaign/azure-health-check' },
    { label: 'NIS2 Use Case', href: '/usecases/nis2-compliance' },
    { label: 'Secure AI Code Use Case', href: '/usecases/secure-ai-generated-code' },
  ];

  const eventMenuItems: MenuItem[] = [
    { label: 'Health Check Release', href: '/events/helium-launch-2026' },
    ...upcomingTalks.map((talk) => ({ label: talk.title, href: talk.url, external: true })),
  ];

  const learnMenuItems: MenuItem[] = [
    { label: 'How Helium Works', href: '/#what-you-see' },
    { label: 'Podcast: DevUP Talks', href: '/#talks' },
    { label: 'Blog', href: 'https://blog.devup.solutions', external: true },
  ];

  const companyMenuItems: MenuItem[] = [
    { label: 'Partner Program', href: '/partners' },
    { label: 'About', href: '/about' },
    { label: 'Contact', href: '/#contact' },
  ];

  const desktopMenuItems: MenuItem[] = [
    { label: 'How Helium Works', href: '/#what-you-see' },
    { label: 'Offers', href: '/#offers' },
    { label: 'About', href: '/about' },
    { label: 'Blog', href: 'https://blog.devup.solutions', external: true },
    { label: 'Contact', href: '/#contact' },
  ];

  const desktopUseCaseItems: MenuItem[] = [
    { label: 'Secure AI Code', href: '/usecases/secure-ai-generated-code' },
    { label: 'NIS2 Compliance', href: '/usecases/nis2-compliance' },
  ];

  const desktopEventItems: MenuItem[] = eventMenuItems;

  const isDesktopUseCasesActive = desktopUseCaseItems.some((item) => item.href === location.pathname);
  const isDesktopEventActive = desktopEventItems.some((item) => item.href === location.pathname);

  const isMenuItemActive = (href: string, external?: boolean) => {
    if (external) {
      return false;
    }

    const [path, hash] = href.split('#');
    const targetPath = path || '/';
    const locationPath = location.pathname || '/';
    const locationHash = location.hash.replace('#', '');

    if (targetPath !== locationPath) {
      return false;
    }

    if (!hash) {
      return true;
    }

    return locationHash === hash;
  };

  const renderMenuGroup = (title: string, items: MenuItem[]) => (
    <li key={title} className="px-2 py-2">
      <p className="px-2 pb-2 text-xs uppercase tracking-[0.2em] text-blue-300">{title}</p>
      <ul className="space-y-1">
        {items.map((item) => {
          const active = isMenuItemActive(item.href, item.external);
          const itemClasses = `block rounded-md px-4 py-3 text-sm font-medium tracking-wide transition-colors duration-150 ${
            active
              ? 'bg-[#123A66] text-white'
              : 'text-[#D9E8FA] hover:bg-[#0C315A] hover:text-white focus-visible:bg-[#123A66] focus-visible:text-white'
          }`;

          return (
            <li key={item.href} className="cursor-pointer">
              <a
                href={item.href}
                target={item.external ? '_blank' : undefined}
                rel={item.external ? 'noopener noreferrer' : undefined}
                onClick={() => setShowMenu(false)}
                className={itemClasses}
              >
                {item.label}
              </a>
            </li>
          );
        })}
      </ul>
    </li>
  );

  return (
    <div className="App text-white">
      <header className='fixed top-0 left-0 z-[1000] w-full bg-[#00182F]/95 backdrop-blur-sm border-b border-[#123A66]'>
        <div className='mx-auto flex min-h-20 w-full max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8'>
          <a href="/" className='inline-flex items-center'>
            <img src={DevUpIcon} className='h-14' alt='DevUp Icon' />
          </a>

          <nav className="hidden items-center gap-1 md:flex" aria-label="Main navigation">
            <div
              ref={desktopUseCasesRef}
              className="relative"
              onMouseEnter={openDesktopUseCasesMenu}
              onMouseLeave={scheduleDesktopUseCasesClose}
              onBlur={(event) => {
                const nextTarget = event.relatedTarget as Node | null;
                if (!nextTarget || !desktopUseCasesRef.current?.contains(nextTarget)) {
                  closeDesktopUseCasesMenu();
                }
              }}
            >
              <button
                className={`inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium tracking-wide transition-colors duration-150 ${
                  isDesktopUseCasesActive
                    ? 'bg-[#123A66] text-white'
                    : 'text-[#D9E8FA] hover:bg-[#0C315A] hover:text-white focus-visible:bg-[#123A66] focus-visible:text-white'
                }`}
                onClick={() => setShowDesktopUseCases((value) => !value)}
                onFocus={openDesktopUseCasesMenu}
                aria-expanded={showDesktopUseCases}
                aria-haspopup="menu"
              >
                Use Cases
                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path
                    fillRule="evenodd"
                    d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.168l3.71-3.94a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06Z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              {showDesktopUseCases ? (
                <div
                  className="absolute left-0 top-full z-[1200] mt-1 w-56 overflow-hidden rounded-lg border border-[#1A487A] bg-[#00182F] shadow-xl shadow-[#00000066]"
                  onMouseEnter={openDesktopUseCasesMenu}
                  onMouseLeave={scheduleDesktopUseCasesClose}
                >
                  <ul className="p-2" role="menu" aria-label="Use case links">
                    {desktopUseCaseItems.map((item) => {
                      const active = isMenuItemActive(item.href);
                      return (
                        <li key={item.href}>
                          <a
                            href={item.href}
                            className={`block rounded-md px-3 py-2 text-sm font-medium transition-colors duration-150 ${
                              active
                                ? 'bg-[#123A66] text-white'
                                : 'text-[#D9E8FA] hover:bg-[#0C315A] hover:text-white focus-visible:bg-[#123A66] focus-visible:text-white'
                            }`}
                            onClick={closeDesktopUseCasesMenu}
                            role="menuitem"
                          >
                            {item.label}
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : null}
            </div>

            <div
              ref={desktopEventRef}
              className="relative"
              onMouseEnter={openDesktopEventMenu}
              onMouseLeave={scheduleDesktopEventClose}
              onBlur={(event) => {
                const nextTarget = event.relatedTarget as Node | null;
                if (!nextTarget || !desktopEventRef.current?.contains(nextTarget)) {
                  closeDesktopEventMenu();
                }
              }}
            >
              <button
                className={`inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium tracking-wide transition-colors duration-150 ${
                  isDesktopEventActive
                    ? 'bg-[#123A66] text-white'
                    : 'text-[#D9E8FA] hover:bg-[#0C315A] hover:text-white focus-visible:bg-[#123A66] focus-visible:text-white'
                }`}
                onClick={() => setShowDesktopEvent((value) => !value)}
                onFocus={openDesktopEventMenu}
                aria-expanded={showDesktopEvent}
                aria-haspopup="menu"
              >
                Events
                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path
                    fillRule="evenodd"
                    d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.168l3.71-3.94a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06Z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              {showDesktopEvent ? (
                <div
                  className="absolute left-0 top-full z-[1200] mt-1 w-56 overflow-hidden rounded-lg border border-[#1A487A] bg-[#00182F] shadow-xl shadow-[#00000066]"
                  onMouseEnter={openDesktopEventMenu}
                  onMouseLeave={scheduleDesktopEventClose}
                >
                  <ul className="p-2" role="menu" aria-label="Event links">
                    {desktopEventItems.map((item) => {
                      const active = isMenuItemActive(item.href, item.external);
                      return (
                        <li key={item.href}>
                          <a
                            href={item.href}
                            target={item.external ? '_blank' : undefined}
                            rel={item.external ? 'noopener noreferrer' : undefined}
                            className={`block rounded-md px-3 py-2 text-sm font-medium transition-colors duration-150 ${
                              active
                                ? 'bg-[#123A66] text-white'
                                : 'text-[#D9E8FA] hover:bg-[#0C315A] hover:text-white focus-visible:bg-[#123A66] focus-visible:text-white'
                            }`}
                            onClick={closeDesktopEventMenu}
                            role="menuitem"
                          >
                            {item.label}
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : null}
            </div>

            {desktopMenuItems.map((item) => {
              const active = isMenuItemActive(item.href, item.external);
              const desktopItemClasses = `rounded-md px-3 py-2 text-sm font-medium tracking-wide transition-colors duration-150 ${
                active
                  ? 'bg-[#123A66] text-white'
                  : 'text-[#D9E8FA] hover:bg-[#0C315A] hover:text-white focus-visible:bg-[#123A66] focus-visible:text-white'
              }`;

              return (
                <a
                  key={item.href}
                  href={item.href}
                  target={item.external ? '_blank' : undefined}
                  rel={item.external ? 'noopener noreferrer' : undefined}
                  className={desktopItemClasses}
                >
                  {item.label}
                </a>
              );
            })}

          </nav>

          <div className="md:hidden">
          <button
            className="rounded-md p-1.5 text-white transition-colors hover:bg-[#123A66] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5BA6FF]"
            onClick={() => setShowMenu(!showMenu)}
            aria-expanded={showMenu}
            aria-controls="menu"
            aria-label="Toggle navigation menu"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7"></path>
            </svg>
          </button>
        </div>
        </div>
        <div
          className={`fixed inset-0 z-[1090] bg-[#00000033] md:hidden ${showMenu ? '' : 'hidden'}`}
          onClick={() => setShowMenu(false)}
          aria-hidden="true"
        />
        <div
          className={`fixed right-4 top-16 z-[1100] w-[min(24rem,calc(100vw-2rem))] overflow-hidden rounded-xl border border-[#1A487A] bg-[#00182F] shadow-2xl shadow-[#00000066] md:hidden ${showMenu ? '' : 'hidden'}`}
          id="menu"
        >
          <ul className="max-h-[70vh] overflow-y-auto p-2 text-left text-white">
            {renderMenuGroup('Offers And Use Cases', offerMenuItems)}
            {renderMenuGroup('Events', eventMenuItems)}
            {renderMenuGroup('Learn', learnMenuItems)}
            {renderMenuGroup('Company', companyMenuItems)}
          </ul>
        </div>

      </header>
      <div onClick={() =>setShowMenu(false)}>
      <Suspense
                        fallback={
                            <h3 className="pt-24 pb-3 ml-28 md:ml-72 lg:ml-[16.5rem] min-h-screen dark:text-white border-l-2 bg-gray-100 dark:bg-[color:var(--darkmode-background)">
                                Loading...
                            </h3>
                        }
                    >
      <Routes>
        <Route path="/" >
          <Route index element={<MainContent />} />
          <Route path="support" element={<SupportContent />}/>     
          <Route path="privacy-policy" element={<PrivacyContent />}/>
          <Route path="campaign/azure-health-check" element={<CampaignLanding />} />
          <Route path="usecases/nis2-compliance" element={<NIS2Compliance />} />
          <Route path="usecases/secure-ai-generated-code" element={<AIProductivityStartsBeforePrompt />} />
          <Route path="usecases/ai-productivity-foundation" element={<AIProductivityStartsBeforePrompt />} />
          <Route path="events/helium-launch-2026" element={<HeliumLaunchEvent />} />
          <Route path="partners" element={<PartnerContent />} />
          <Route path="about" element={<AboutContent />} />
          <Route path="*" element={<MainContent />} />   
        </Route>
      </Routes>
      </Suspense>
      </div>
    </div>
  );
}

export default App;
